import * as THREE from 'three';
import {
  CAD_TO_THREEJS_MATRIX,
  CadOrigin,
  firstPoint,
  localPositions
} from '../Meshes/geometryLoader';

/**
 * Per-entity scene produced by the backend DUF converter. Each entity carries its own colour, layer
 * and (for solids) attributes, so the canvas can render the whole file with real Deswik colours.
 *   t: 's' = solid (mesh, indexed), 'l' = line/string (polyline)
 *   c: '#rrggbb'   p: flat positions [x,y,z,...]   i: triangle indices (solids)
 *   a: attribute map (solids)   layer: source layer name
 */
export interface TunnelSceneEntity {
  t: 's' | 'l';
  c: string;
  d?: number; // 1 = dashed line type
  layer?: string;
  p: number[];
  i?: number[];
  a?: Record<string, string>;
}

// Dash sizes are in CAD units; dash count is therefore scale-independent (looks dotted at any zoom).
const LINE_DASH_SIZE = 1.5;
const LINE_GAP_SIZE = 1.5;

export interface TunnelScene {
  entities: TunnelSceneEntity[];
}

export const isSceneSource = (sourceName?: string): boolean => (sourceName ?? '').split('?')[0].toLowerCase().endsWith('.json');

export const parseTunnelScene = (text: string): TunnelScene | null => {
  try {
    const parsed = JSON.parse(text) as TunnelScene;
    return Array.isArray(parsed?.entities) ? parsed : null;
  } catch {
    return null;
  }
};

/** CAD-space bounding box across all entity positions (used for camera framing). */
export const tunnelSceneBoundingBox = (scene: TunnelScene): THREE.Box3 => {
  const box = new THREE.Box3();
  const v = new THREE.Vector3();
  scene.entities.forEach((entity) => {
    for (let i = 0; i + 2 < entity.p.length; i += 3) {
      box.expandByPoint(v.set(entity.p[i], entity.p[i + 1], entity.p[i + 2]));
    }
  });
  return box;
};

const buildSolidGeometry = (entity: TunnelSceneEntity, origin: CadOrigin): THREE.BufferGeometry => {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(localPositions(entity.p, origin), 3));
  if (entity.i && entity.i.length > 0) geometry.setIndex(entity.i);
  geometry.applyMatrix4(CAD_TO_THREEJS_MATRIX);
  geometry.computeVertexNormals();
  return geometry;
};

const buildLineGeometry = (entity: TunnelSceneEntity, origin: CadOrigin): THREE.BufferGeometry => {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(localPositions(entity.p, origin), 3));
  geometry.applyMatrix4(CAD_TO_THREEJS_MATRIX);
  return geometry;
};

export interface BuiltTunnelScene {
  object: THREE.Group;
  meshMaterials: THREE.MeshPhongMaterial[];
  /** CAD point the geometry is relative to; place the object there. */
  origin: CadOrigin;
}

/**
 * Build the scene as a Three group: one mesh per solid and one line per string, each in its own
 * Deswik colour. Materials are shared per colour to keep the material count low; solid materials are
 * returned so opacity can be updated without rebuilding. Attributes/layer are stashed on userData
 * for a future hover/inspect feature.
 */
export const buildTunnelSceneObject = (scene: TunnelScene, opacity: number): BuiltTunnelScene => {
  const group = new THREE.Group();
  const meshMaterialsByColor = new Map<string, THREE.MeshPhongMaterial>();
  const lineMaterialsByColor = new Map<string, THREE.LineBasicMaterial>();
  const transparent = opacity < 1;
  const origin = firstPoint(scene.entities.find((entity) => entity.p.length >= 3)?.p ?? []);

  scene.entities.forEach((entity) => {
    if (entity.t === 'l') {
      const dashed = entity.d === 1;
      const key = dashed ? `${entity.c}:d` : entity.c;
      let material = lineMaterialsByColor.get(key);
      if (!material) {
        material = dashed
          ? new THREE.LineDashedMaterial({ color: new THREE.Color(entity.c), dashSize: LINE_DASH_SIZE, gapSize: LINE_GAP_SIZE })
          : new THREE.LineBasicMaterial({ color: new THREE.Color(entity.c) });
        lineMaterialsByColor.set(key, material);
      }
      const line = new THREE.Line(buildLineGeometry(entity, origin), material);
      if (dashed) line.computeLineDistances();
      line.userData = { layer: entity.layer, attributes: entity.a };
      group.add(line);
      return;
    }

    let material = meshMaterialsByColor.get(entity.c);
    if (!material) {
      material = new THREE.MeshPhongMaterial({
        color: new THREE.Color(entity.c),
        side: THREE.DoubleSide,
        transparent,
        opacity,
        depthWrite: opacity >= 1,
      });
      meshMaterialsByColor.set(entity.c, material);
    }
    const mesh = new THREE.Mesh(buildSolidGeometry(entity, origin), material);
    mesh.userData = { layer: entity.layer, attributes: entity.a };
    group.add(mesh);
  });

  return { object: group, meshMaterials: Array.from(meshMaterialsByColor.values()), origin };
};
